/**
 * Reviews Service
 * 
 * Handles all review-related operations including:
 * - Creating and managing product reviews
 * - Calculating store ratings
 * - Retrieving reviews for display
 * - Admin moderation functions
 */

import { db } from '../firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';

// Collection references
const reviewsCollection = 'reviews';
const storesCollection = 'shops';

/**
 * Create a new review for a product
 * @param {Object} reviewData - Review data
 * @param {string} reviewData.orderId - Order ID
 * @param {string} reviewData.orderGroupId - Order group ID
 * @param {string} reviewData.productId - Product ID
 * @param {string} reviewData.customerId - Customer ID
 * @param {number} reviewData.rating - Rating (1-5)
 * @param {string} reviewData.comment - Optional comment
 * @returns {Promise<string>} Review ID
 */
export async function createReview(reviewData) {
  try {
    const { orderId, orderGroupId, productId, customerId, rating, comment } = reviewData;
    
    // Validate rating
    if (!rating || rating < 1 || rating > 5) {
      throw new Error('Rating must be between 1 and 5');
    }
    
    // Check if review already exists for this order/product/customer combo
    const existingReviewQuery = query(
      collection(db, reviewsCollection),
      where('orderId', '==', orderId),
      where('productId', '==', productId),
      where('customerId', '==', customerId),
      where('status', '==', 'active')
    );
    
    const existingReviews = await getDocs(existingReviewQuery);
    if (!existingReviews.empty) {
      throw new Error('Review already exists for this product in this order');
    }
    
    // Get customer first name for the review
    let customerName = 'Anonymous';
    if (customerId) {
      try {
        const customerDoc = await getDoc(doc(db, 'users', customerId));
        if (customerDoc.exists()) {
          const customerData = customerDoc.data();
          const fullName = customerData.displayName || 
                          customerData.firstName || 
                          customerData.name ||
                          customerData.email?.split('@')[0] || // Use email prefix if no name
                          'Anonymous';
          
          // Extract first name only
          customerName = fullName.split(' ')[0] || 'Anonymous';
          console.log('🔍 Customer first name stored in review:', customerName);
        }
      } catch (error) {
        console.warn('⚠️ Could not fetch customer name for review creation:', error);
        customerName = 'Anonymous';
      }
    }

    // Create review document
    const reviewDoc = {
      orderId,
      orderGroupId,
      productId,
      customerId,
      customerName, // Store customer name directly in review
      rating,
      comment: comment || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'active'
    };
    
    const docRef = await addDoc(collection(db, reviewsCollection), reviewDoc);
    
    // Update store rating (non-blocking)
    updateStoreRating(productId).catch(error => {
      console.warn('⚠️ Store rating update failed (non-critical):', error);
    });
    
    console.log('✅ Review created successfully:', docRef.id);
    return docRef.id;
    
  } catch (error) {
    console.error('❌ Error creating review:', error);
    throw error;
  }
}

/**
 * Update an existing review
 * @param {string} reviewId - Review ID
 * @param {Object} updateData - Update data
 * @param {number} updateData.rating - New rating
 * @param {string} updateData.comment - New comment
 * @returns {Promise<void>}
 */
export async function updateReview(reviewId, updateData) {
  try {
    const { rating, comment } = updateData;
    
    // Validate rating
    if (rating && (rating < 1 || rating > 5)) {
      throw new Error('Rating must be between 1 and 5');
    }
    
    // Get the review to find the product ID for store rating update
    const reviewDoc = await getDoc(doc(db, reviewsCollection, reviewId));
    if (!reviewDoc.exists()) {
      throw new Error('Review not found');
    }
    
    const reviewData = reviewDoc.data();
    
    // Update review
    await updateDoc(doc(db, reviewsCollection, reviewId), {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    
    // Update store rating (non-blocking)
    updateStoreRating(reviewData.productId).catch(error => {
      console.warn('⚠️ Store rating update failed (non-critical):', error);
    });
    
    console.log('✅ Review updated successfully:', reviewId);
    
  } catch (error) {
    console.error('❌ Error updating review:', error);
    throw error;
  }
}

/**
 * Delete a review (soft delete by setting status to 'hidden')
 * @param {string} reviewId - Review ID
 * @returns {Promise<void>}
 */
export async function deleteReview(reviewId) {
  try {
    // Get the review to find the product ID for store rating update
    const reviewDoc = await getDoc(doc(db, reviewsCollection, reviewId));
    if (!reviewDoc.exists()) {
      throw new Error('Review not found');
    }
    
    const reviewData = reviewDoc.data();
    
    // Soft delete by setting status to 'hidden'
    await updateDoc(doc(db, reviewsCollection, reviewId), {
      status: 'hidden',
      updatedAt: serverTimestamp()
    });
    
    // Update store rating (non-blocking)
    updateStoreRating(reviewData.productId).catch(error => {
      console.warn('⚠️ Store rating update failed (non-critical):', error);
    });
    
    console.log('✅ Review deleted successfully:', reviewId);
    
        } catch (error) {
    console.error('❌ Error deleting review:', error);
    throw error;
  }
}

/**
 * Get reviews for a specific product
 * @param {string} productId - Product ID
 * @param {number} limitCount - Number of reviews to return
 * @param {number} offset - Number of reviews to skip (for pagination)
 * @returns {Promise<Array>} Array of reviews
 */
export async function getProductReviews(productId, limitCount = 10, offset = 0) {
  try {
    console.log('🔍 getProductReviews called with productId:', productId);
    
    // Simplified query to avoid index requirements
    const reviewsQuery = query(
      collection(db, reviewsCollection),
      where('productId', '==', productId),
      where('status', '==', 'active')
    );
    
    console.log('🔍 Executing Firebase query for productId:', productId);
    const reviewsSnapshot = await getDocs(reviewsQuery);
    console.log('🔍 Query executed, docs count:', reviewsSnapshot.docs.length);
    
    const reviews = reviewsSnapshot.docs.map(doc => {
      const data = doc.data();
      console.log('🔍 Product review doc:', { id: doc.id, productId: data.productId, rating: data.rating, orderId: data.orderId });
    return {
        id: doc.id,
        ...data
      };
    });
    
    // Sort by createdAt on the client side to avoid index requirement
    reviews.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt);
      const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt);
      return bTime - aTime; // Descending order (newest first)
    });
    
    // Apply offset and limit after sorting
    const paginatedReviews = reviews.slice(offset, offset + limitCount);
    
    console.log(`📝 Retrieved ${paginatedReviews.length} reviews for product ${productId} (offset: ${offset}, limit: ${limitCount})`);
    return paginatedReviews;

  } catch (error) {
    console.error('❌ Error getting product reviews:', error);
    throw error;
  }
}

/**
 * Get reviews for a specific order
 * @param {string} orderId - Order ID
 * @returns {Promise<Array>} Array of reviews
 */
export async function getOrderReviews(orderId) {
  try {
    console.log('🔍 getOrderReviews called with orderId:', orderId);
    
    // Simplified query to avoid index requirements
    const reviewsQuery = query(
      collection(db, reviewsCollection),
      where('orderId', '==', orderId),
      where('status', '==', 'active')
    );
    
    console.log('🔍 Executing Firebase query for orderId:', orderId);
    const reviewsSnapshot = await getDocs(reviewsQuery);
    console.log('🔍 Query executed, docs count:', reviewsSnapshot.docs.length);
    
    const reviews = reviewsSnapshot.docs.map(doc => {
      const data = doc.data();
      console.log('🔍 Review doc:', { id: doc.id, orderId: data.orderId, productId: data.productId, rating: data.rating });
      return {
        id: doc.id,
        ...data
      };
    });
    
    // Sort by createdAt on the client side to avoid index requirement
    reviews.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt);
      const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt);
      return bTime - aTime; // Descending order (newest first)
    });
    
    console.log(`📝 Retrieved ${reviews.length} reviews for order ${orderId}`);
    return reviews;
    
  } catch (error) {
    console.error('❌ Error getting order reviews:', error);
    throw error;
  }
}

/**
 * Get reviews by a specific customer
 * @param {string} customerId - Customer ID
 * @param {number} limitCount - Number of reviews to return
 * @returns {Promise<Array>} Array of reviews
 */
export async function getCustomerReviews(customerId, limitCount = 20) {
  try {
    const reviewsQuery = query(
      collection(db, reviewsCollection),
      where('customerId', '==', customerId),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const reviewsSnapshot = await getDocs(reviewsQuery);
    const reviews = reviewsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`📝 Retrieved ${reviews.length} reviews for customer ${customerId}`);
    return reviews;
    
  } catch (error) {
    console.error('❌ Error getting customer reviews:', error);
    throw error;
  }
}

/**
 * Calculate and update store rating based on product reviews
 * @param {string} productId - Product ID
 * @returns {Promise<void>}
 */
export async function updateStoreRating(productId) {
  try {
    // First, get the product to find its store
    const productDoc = await getDoc(doc(db, 'products', productId));
    if (!productDoc.exists()) {
      console.warn('⚠️ Product not found for rating update:', productId);
      return;
    }
    
    const productData = productDoc.data();
    const storeId = productData.sellerId || productData.storeId;
    
    if (!storeId) {
      console.warn('⚠️ No store ID found for product:', productId);
      return;
    }
    
    // Get all active reviews for products from this store
    const storeProductsQuery = query(
      collection(db, 'products'),
      where('sellerId', '==', storeId)
    );
    
    const storeProductsSnapshot = await getDocs(storeProductsQuery);
    const productIds = storeProductsSnapshot.docs.map(doc => doc.id);
    
    if (productIds.length === 0) {
      console.warn('⚠️ No products found for store:', storeId);
      return;
    }
    
    // Get all reviews for store products
    const reviewsQuery = query(
      collection(db, reviewsCollection),
      where('productId', 'in', productIds),
      where('status', '==', 'active')
    );
    
    const reviewsSnapshot = await getDocs(reviewsQuery);
    const reviews = reviewsSnapshot.docs.map(doc => doc.data());

    // Calculate average rating
    let totalRating = 0;
    let reviewCount = 0;

    reviews.forEach(review => {
      totalRating += review.rating;
      reviewCount++;
    });
    
    const avgRating = reviewCount > 0 ? totalRating / reviewCount : 0;
    const lastReviewedAt = reviewCount > 0 ? new Date() : null;
    
    // Update store document (with error handling)
    try {
      const storeRef = doc(db, storesCollection, storeId);
      await updateDoc(storeRef, {
        avgRating: Math.round(avgRating * 10) / 10, // Round to 1 decimal place
        reviewCount,
        lastReviewedAt: lastReviewedAt ? serverTimestamp() : null
      });
      
      console.log(`✅ Store rating updated for store ${storeId}: ${avgRating.toFixed(1)} (${reviewCount} reviews)`);
    } catch (storeUpdateError) {
      console.warn('⚠️ Could not update store rating (non-critical):', storeUpdateError);
      // Don't throw - this is non-critical for review creation
    }
    
  } catch (error) {
    console.error('❌ Error updating store rating:', error);
    throw error;
  }
}

/**
 * Get store rating information
 * @param {string} storeId - Store ID
 * @returns {Promise<Object>} Store rating data
 */
export async function getStoreRating(storeId) {
  try {
    console.log('🔍 getStoreRating called for storeId:', storeId);
    const storeDoc = await getDoc(doc(db, storesCollection, storeId));
    
    if (!storeDoc.exists()) {
      console.log('⚠️ Store document does not exist for storeId:', storeId);
      return {
        avgRating: 0,
        reviewCount: 0,
        lastReviewedAt: null
      };
    }
    
    const storeData = storeDoc.data();
    console.log('🔍 Store data retrieved:', {
      storeId,
      avgRating: storeData.avgRating,
      reviewCount: storeData.reviewCount,
      lastReviewedAt: storeData.lastReviewedAt
    });
    
    return {
      avgRating: storeData.avgRating || 0,
      reviewCount: storeData.reviewCount || 0,
      lastReviewedAt: storeData.lastReviewedAt
    };

  } catch (error) {
    console.error('❌ Error getting store rating:', error);
    throw error;
  }
}

/**
 * Create multiple reviews for an order (batch operation)
 * @param {Array} reviewsData - Array of review data objects
 * @returns {Promise<Array>} Array of created review IDs
 */
export async function createMultipleReviews(reviewsData) {
  try {
    const batch = writeBatch(db);
    const reviewIds = [];
    
    // Validate all reviews first
    for (const reviewData of reviewsData) {
      const { orderId, productId, customerId, rating } = reviewData;
      
      if (!rating || rating < 1 || rating > 5) {
        throw new Error(`Invalid rating for product ${productId}: ${rating}`);
      }
      
      // Check for existing reviews
      const existingReviewQuery = query(
        collection(db, reviewsCollection),
        where('orderId', '==', orderId),
        where('productId', '==', productId),
        where('customerId', '==', customerId),
        where('status', '==', 'active')
      );
      
      const existingReviews = await getDocs(existingReviewQuery);
      if (!existingReviews.empty) {
        throw new Error(`Review already exists for product ${productId} in order ${orderId}`);
      }
    }
    
    // Get unique customer IDs and fetch their names
    const uniqueCustomerIds = [...new Set(reviewsData.map(r => r.customerId).filter(Boolean))];
    const customerNames = {};
    
    for (const customerId of uniqueCustomerIds) {
      try {
        const customerDoc = await getDoc(doc(db, 'users', customerId));
        if (customerDoc.exists()) {
          const customerData = customerDoc.data();
          const fullName = customerData.displayName || 
                          customerData.firstName || 
                          customerData.name ||
                          customerData.email?.split('@')[0] || 
                          'Anonymous';
          
          // Extract first name only
          customerNames[customerId] = fullName.split(' ')[0] || 'Anonymous';
        } else {
          customerNames[customerId] = 'Anonymous';
        }
      } catch (error) {
        console.warn('⚠️ Could not fetch customer name for batch review:', customerId, error);
        customerNames[customerId] = 'Anonymous';
      }
    }

    // Create reviews in batch
    for (const reviewData of reviewsData) {
      const { orderId, orderGroupId, productId, customerId, rating, comment } = reviewData;
      
      const reviewRef = doc(collection(db, reviewsCollection));
      batch.set(reviewRef, {
        orderId,
        orderGroupId,
        productId,
        customerId,
        customerName: customerNames[customerId] || 'Anonymous', // Store customer name
        rating,
        comment: comment || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'active'
      });
      
      reviewIds.push(reviewRef.id);
    }
    
    await batch.commit();
    
    // Update store ratings for all affected products
    const uniqueProductIds = [...new Set(reviewsData.map(r => r.productId))];
    for (const productId of uniqueProductIds) {
      await updateStoreRating(productId);
    }
    
    console.log(`✅ Created ${reviewIds.length} reviews successfully`);
    return reviewIds;
    
  } catch (error) {
    console.error('❌ Error creating multiple reviews:', error);
    throw error;
  }
}

/**
 * Check if a customer can review a product in an order
 * @param {string} orderId - Order ID
 * @param {string} productId - Product ID
 * @param {string} customerId - Customer ID
 * @returns {Promise<boolean>} Whether review is allowed
 */
export async function canReviewProduct(orderId, productId, customerId) {
  try {
    // Check if review already exists
    const existingReviewQuery = query(
      collection(db, reviewsCollection),
      where('orderId', '==', orderId),
      where('productId', '==', productId),
      where('customerId', '==', customerId),
      where('status', '==', 'active')
    );
    
    const existingReviews = await getDocs(existingReviewQuery);
    return existingReviews.empty;
    
  } catch (error) {
    console.error('❌ Error checking review eligibility:', error);
    return false;
  }
}

/**
 * Get store rating summary with additional metadata
 * @param {string} storeId - Store ID
 * @returns {Promise<Object>} Store rating summary
 */
export async function getStoreRatingSummary(storeId) {
  try {
    console.log('🔍 getStoreRatingSummary called for storeId:', storeId);
    const rating = await getStoreRating(storeId);
    console.log('🔍 Rating data from getStoreRating:', rating);
    
    // Get additional metadata
    const storeDoc = await getDoc(doc(db, storesCollection, storeId));
    const storeData = storeDoc.exists() ? storeDoc.data() : {};
    console.log('🔍 Store metadata:', { storeName: storeData.name, storeId });
    
    // Calculate rating breakdown
    const ratingBreakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    
    if (rating.reviewCount > 0) {
      try {
        // Get all reviews for this store to calculate breakdown
        const reviews = await getStoreReviews(storeId, 100); // Get up to 100 reviews for breakdown
        
        reviews.forEach(review => {
          console.log('🔍 Processing review for breakdown:', { rating: review.rating, productId: review.productId });
          if (review.rating >= 1 && review.rating <= 5) {
            ratingBreakdown[review.rating]++;
            console.log(`🔍 Added to breakdown: ${review.rating} stars, count now: ${ratingBreakdown[review.rating]}`);
          }
        });
        
        console.log('🔍 Rating breakdown calculated:', ratingBreakdown);
      } catch (error) {
        console.warn('⚠️ Could not calculate rating breakdown:', error);
      }
    }
    
    const result = {
      ...rating,
      storeName: storeData.name || 'Unknown Store',
      storeId,
      lastUpdated: rating.lastReviewedAt,
      ratingBreakdown
    };
    
    console.log('🔍 Final getStoreRatingSummary result:', result);
    return result;
    
  } catch (error) {
    console.error('❌ Error getting store rating summary:', error);
    throw error;
  }
}

/**
 * Get all products for a store
 * @param {string} storeId - Store ID
 * @returns {Promise<Array>} Array of products
 */
export async function getStoreProducts(storeId) {
  try {
    const productsQuery = query(
      collection(db, 'products'),
      where('sellerId', '==', storeId)
    );
    
    const productsSnapshot = await getDocs(productsQuery);
    const products = productsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`📦 Retrieved ${products.length} products for store ${storeId}`);
    return products;
    
  } catch (error) {
    console.error('❌ Error getting store products:', error);
    throw error;
  }
}

/**
 * Get all reviews for a store (across all products)
 * @param {string} storeId - Store ID
 * @param {number} limitCount - Number of reviews to return
 * @returns {Promise<Array>} Array of reviews
 */
export async function getStoreReviews(storeId, limitCount = 50) {
  try {
    // First get all products for the store
    const products = await getStoreProducts(storeId);
    const productIds = products.map(p => p.id);
    
    if (productIds.length === 0) {
      return [];
    }
    
    // Get reviews for all store products
    const reviewsQuery = query(
      collection(db, reviewsCollection),
      where('productId', 'in', productIds),
      where('status', '==', 'active')
    );
    
    const reviewsSnapshot = await getDocs(reviewsQuery);
    const reviews = reviewsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Enrich reviews with product and customer information
    console.log('🔍 Processing reviews for enrichment:', reviews.map(r => ({ id: r.id, customerId: r.customerId, rating: r.rating, comment: r.comment })));
    const enrichedReviews = await Promise.all(reviews.map(async (review) => {
      console.log('🔍 Enriching review:', { id: review.id, customerId: review.customerId, rating: review.rating, comment: review.comment });
      const product = products.find(p => p.id === review.productId);
      
      // Use stored customer name from review document
      const customerName = review.customerName || 'Anonymous';
      console.log('🔍 Using stored customer name:', customerName, 'for review:', review.id);
      
      return {
        ...review,
        productName: product?.name || 'Unknown Product',
        productImage: product?.image,
        customerName
      };
    }));
    
    // Sort by createdAt descending (newest first)
    enrichedReviews.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt);
      const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt);
      return bTime - aTime;
    });
    
    // Apply limit after sorting
    const limitedReviews = enrichedReviews.slice(0, limitCount);
    
    console.log(`📝 Retrieved ${limitedReviews.length} reviews for store ${storeId}`);
    return limitedReviews;
    
  } catch (error) {
    console.error('❌ Error getting store reviews:', error);
    throw error;
  }
}

/**
 * Manually refresh store rating for a specific store
 * @param {string} storeId - Store ID
 * @returns {Promise<void>}
 */
export async function refreshStoreRating(storeId) {
  try {
    console.log('🔄 Manually refreshing store rating for store:', storeId);
    
    // Get all products for this store
    const storeProductsQuery = query(
      collection(db, 'products'),
      where('sellerId', '==', storeId),
      where('status', '==', true)
    );
    
    const storeProductsSnapshot = await getDocs(storeProductsQuery);
    const productIds = storeProductsSnapshot.docs.map(doc => doc.id);
    
    if (productIds.length === 0) {
      console.warn('⚠️ No products found for store:', storeId);
      return;
    }
    
    // Get all reviews for store products
    const reviewsQuery = query(
      collection(db, reviewsCollection),
      where('productId', 'in', productIds),
      where('status', '==', 'active')
    );
    
    const reviewsSnapshot = await getDocs(reviewsQuery);
    const reviews = reviewsSnapshot.docs.map(doc => doc.data());

    // Calculate average rating
    let totalRating = 0;
    let reviewCount = 0;

    reviews.forEach(review => {
      totalRating += review.rating;
      reviewCount++;
    });
    
    const avgRating = reviewCount > 0 ? totalRating / reviewCount : 0;
    const lastReviewedAt = reviewCount > 0 ? new Date() : null;
    
    // Update store document
    const storeRef = doc(db, storesCollection, storeId);
    await updateDoc(storeRef, {
      avgRating: Math.round(avgRating * 10) / 10, // Round to 1 decimal place
      reviewCount,
      lastReviewedAt: lastReviewedAt ? serverTimestamp() : null
    });
    
    console.log(`✅ Store rating refreshed for store ${storeId}: ${avgRating.toFixed(1)} (${reviewCount} reviews)`);
    
  } catch (error) {
    console.error('❌ Error refreshing store rating:', error);
    throw error;
  }
}

/**
 * Debug function to check all reviews in the database
 * @returns {Promise<Array>} All reviews
 */
export async function getAllReviews() {
  try {
    console.log('🔍 getAllReviews called - checking all reviews in database');
    
    const reviewsQuery = query(
      collection(db, reviewsCollection),
      where('status', '==', 'active')
    );
    
    const reviewsSnapshot = await getDocs(reviewsQuery);
    const reviews = reviewsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`📝 Found ${reviews.length} total reviews in database:`, reviews.map(r => ({
      id: r.id,
      productId: r.productId,
      orderId: r.orderId,
      rating: r.rating,
      customerId: r.customerId
    })));
    
    return reviews;

  } catch (error) {
    console.error('❌ Error getting all reviews:', error);
    throw error;
  }
}

/**
 * Format review date for display
 * @param {Object} timestamp - Firestore timestamp or Date
 * @returns {string} Formatted date string
 */
export function formatReviewDate(timestamp) {
  try {
    let date;
    
    if (timestamp && timestamp.toDate) {
      // Firestore Timestamp
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      return 'Unknown date';
    }
    
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    }
    
  } catch (error) {
    console.error('❌ Error formatting review date:', error);
    return 'Unknown date';
  }
}